import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Stack, Text, PrimaryButton, MessageBar, MessageBarType } from "@fluentui/react";
import { isAxiosError } from "axios";
import { requeueDeadLetter } from "@/api/jobEngine";
import { useDeadLetter } from "./useDeadLetter";
import { useJobEngineConnectionState } from "./useJobEngineConnectionState";
import ConnectionStatusBadge from "./ConnectionStatusBadge";
import PayloadViewer from "./PayloadViewer";
import { formatDateTime } from "./formatters";

const DeadLetterDetailPage: React.FC = () => {
  const { deadLetterId = "" } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useDeadLetter(deadLetterId);
  const isConnected = useJobEngineConnectionState();
  const [status, setStatus] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const onRequeue = async () => {
    setStatus(null);
    try {
      await requeueDeadLetter(deadLetterId);
      setStatus({ type: "success", text: "Requeued" });
      window.setTimeout(() => navigate("/job-engine/dead-letters"), 1200);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 403) setStatus({ type: "error", text: "You don't have permission to requeue dead letters." });
      else if (isAxiosError(err)) setStatus({ type: "error", text: err.response?.data?.title ?? "Requeue failed" });
      else setStatus({ type: "error", text: "An unexpected error occurred." });
    }
  };

  return (
    <div style={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
    <Stack tokens={{ childrenGap: 16 }}>
      <div className="page-header">
        <h2 className="page-title">Dead letter {deadLetterId}</h2>
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
          <PrimaryButton text="Requeue" onClick={onRequeue} disabled={isLoading || !data} />
          <ConnectionStatusBadge isConnected={isConnected} />
        </Stack>
      </div>

      {status && <MessageBar messageBarType={status.type === "success" ? MessageBarType.success : MessageBarType.error}>{status.text}</MessageBar>}
      {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}

      {data && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Text variant="small">Type: {data.jobType}</Text>
          <Text variant="small">Dead-lettered at: {formatDateTime(data.deadLetteredAt)}</Text>
          <Text variant="small">Correlation: {data.correlationId}</Text>

          <Stack tokens={{ childrenGap: 6 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Payload</Text>
            <PayloadViewer value={data.payload} />
          </Stack>

          <Stack tokens={{ childrenGap: 6 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Error</Text>
            <div style={{ fontFamily: "monospace", whiteSpace: "pre-wrap", padding: 8, border: "1px solid #edebe9", borderRadius: 4, background: "#faf9f8", maxHeight: 360, overflow: "auto" }}>
              {data.errorFull}
            </div>
          </Stack>
        </Stack>
      )}
    </Stack>
    </div>
  );
};

export default DeadLetterDetailPage;
